import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  AlertTriangle, 
  CheckCircle, 
  AlertCircle,
  Info,
  TrendingDown,
  TrendingUp,
  Clock,
  Briefcase,
  Calendar,
  DollarSign,
  FileText
} from 'lucide-react';

/**
 * Componente per visualizzare i risultati della verifica CCNL avanzata
 */
export default function CCNLVerificationResult({ verification }) {
  if (!verification) return null;

  const {
    is_valid,
    total_issues,
    issues = [],
    warnings = [],
    calculations = {},
    summary = {},
    ccnl_identified
  } = verification;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 border-red-300 text-red-800';
      case 'high': return 'bg-orange-100 border-orange-300 text-orange-800';
      case 'medium': return 'bg-amber-100 border-amber-300 text-amber-800';
      case 'low': return 'bg-blue-100 border-blue-300 text-blue-800';
      default: return 'bg-gray-100 border-gray-300 text-gray-800';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'high': return <AlertTriangle className="w-5 h-5 text-orange-600" />;
      case 'medium': return <AlertTriangle className="w-5 h-5 text-amber-600" />;
      case 'low': return <Info className="w-5 h-5 text-blue-600" />;
      default: return <Info className="w-5 h-5 text-gray-600" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header con stato verifica */}
      <Card className={`swiss-card ${is_valid ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${is_valid ? 'bg-emerald-100' : 'bg-red-100'}`}>
              {is_valid ? (
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              ) : (
                <AlertTriangle className="w-8 h-8 text-red-600" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold">
                {is_valid ? 'Verifica CCNL Superata' : `${total_issues} Anomalie Rilevate`}
              </h2>
              <p className="text-muted-foreground">
                CCNL identificato: <span className="font-medium">{ccnl_identified}</span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Differenza Retributiva (se presente) */}
      {summary.differenza_retributiva && (
        <Card className="swiss-card border-amber-200 bg-amber-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <TrendingDown className="w-5 h-5" />
              Differenza Retributiva Stimata
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="text-center p-4 bg-white rounded-lg border border-amber-200">
                <p className="text-sm text-muted-foreground mb-1">Differenza Mensile</p>
                <p className="text-3xl font-bold text-amber-700">
                  {formatCurrency(summary.differenza_retributiva.mensile)}
                </p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg border border-amber-200">
                <p className="text-sm text-muted-foreground mb-1">Differenza Annuale</p>
                <p className="text-3xl font-bold text-amber-700">
                  {formatCurrency(summary.differenza_retributiva.annuale)}
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm text-amber-800 bg-white/50 p-3 rounded">
              {summary.differenza_retributiva.messaggio}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Issues */}
      {issues.length > 0 && (
        <Card className="swiss-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              Anomalie Rilevate ({issues.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {issues.map((issue, index) => (
              <div 
                key={index} 
                className={`p-4 rounded-lg border ${getSeverityColor(issue.severity)}`}
              >
                <div className="flex items-start gap-3">
                  {getSeverityIcon(issue.severity)}
                  <div className="flex-1">
                    <p className="font-medium">{issue.message}</p>
                    {issue.differenza_mensile && (
                      <p className="text-sm mt-1">
                        Differenza: <span className="font-mono font-bold">{formatCurrency(issue.differenza_mensile)}/mese</span>
                        {issue.differenza_annuale && (
                          <span className="ml-2">
                            ({formatCurrency(issue.differenza_annuale)}/anno)
                          </span>
                        )}
                      </p>
                    )}
                    {issue.dettagli && (
                      <div className="mt-2 text-sm bg-white/50 p-2 rounded">
                        <p>Minimo previsto: {formatCurrency(issue.dettagli.minimo_previsto)}</p>
                        <p>Retribuzione attuale: {formatCurrency(issue.dettagli.retribuzione_attuale)}</p>
                        <p>Livello: {issue.dettagli.livello}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <Card className="swiss-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Avvertenze ({warnings.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {warnings.map((warning, index) => (
              <div 
                key={index} 
                className={`p-4 rounded-lg border ${getSeverityColor(warning.severity)}`}
              >
                <div className="flex items-start gap-3">
                  {getSeverityIcon(warning.severity)}
                  <div className="flex-1">
                    <p className="font-medium">{warning.message}</p>
                    {warning.livello_suggerito && (
                      <p className="text-sm mt-1">
                        Livello suggerito: <span className="font-bold">{warning.livello_suggerito}</span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Calcoli Dettagliati */}
      {Object.keys(calculations).length > 0 && (
        <Card className="swiss-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Dettagli Verifica
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Verifica Livello */}
            {calculations.livello_verifica && (
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Verifica Livello e Retribuzione
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Livello</p>
                    <p className="font-bold">{calculations.livello_verifica.livello_normalizzato || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Minimo CCNL</p>
                    <p className="font-mono font-bold">{formatCurrency(calculations.livello_verifica.minimo_tabellare_ccnl)}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">In Busta</p>
                    <p className="font-mono font-bold">{formatCurrency(calculations.livello_verifica.retribuzione_busta)}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Paga Base CCNL</p>
                    <p className="font-mono font-bold">{formatCurrency(calculations.livello_verifica.paga_base_ccnl)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Verifica Straordinari */}
            {calculations.straordinari && (
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Verifica Straordinari
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Ore</p>
                    <p className="font-bold">{calculations.straordinari.ore}h</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Maggiorazione</p>
                    <p className="font-bold">{calculations.straordinari.maggiorazione_prevista}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Importo Previsto</p>
                    <p className="font-mono font-bold">{formatCurrency(calculations.straordinari.importo_previsto)}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">In Busta</p>
                    <p className="font-mono font-bold">{formatCurrency(calculations.straordinari.importo_busta)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Verifica Anzianità */}
            {calculations.anzianita && (
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Verifica Anzianità e Scatti
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Anni Anzianità</p>
                    <p className="font-bold">{calculations.anzianita.anni_anzianita}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Scatti Previsti</p>
                    <p className="font-bold">{calculations.anzianita.scatti_previsti}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Scatti in Busta</p>
                    <p className="font-bold">{calculations.anzianita.scatti_in_busta}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Importo Singolo Scatto</p>
                    <p className="font-mono font-bold">{formatCurrency(calculations.anzianita.importo_singolo_scatto)}</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Periodicità: {calculations.anzianita.periodicita_scatti}
                </p>
              </div>
            )}

            {/* Verifica Ferie */}
            {calculations.ferie && (
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Verifica Ferie
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Maturate (Busta)</p>
                    <p className="font-bold">{calculations.ferie.ferie_maturate_busta} gg</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Previste (Calcolo)</p>
                    <p className="font-bold">{calculations.ferie.ferie_previste_calcolo} gg</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Giorni Annui CCNL</p>
                    <p className="font-bold">{calculations.ferie.giorni_annui_ccnl} gg</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Residue</p>
                    <p className="font-bold">{calculations.ferie.ferie_residue} gg</p>
                  </div>
                </div>
              </div>
            )}

            {/* Livello Suggerito */}
            {calculations.livello_suggerito && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h4 className="font-medium mb-3 flex items-center gap-2 text-amber-800">
                  <TrendingUp className="w-4 h-4" />
                  Livello Corretto Suggerito
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-muted-foreground">Livello Attuale</p>
                    <p className="font-bold text-red-600">{calculations.livello_suggerito.livello_attuale}</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-muted-foreground">Livello Corretto</p>
                    <p className="font-bold text-emerald-600">{calculations.livello_suggerito.livello_corretto}</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-muted-foreground">Diff. Mensile</p>
                    <p className="font-mono font-bold text-amber-700">{formatCurrency(calculations.livello_suggerito.differenza_mensile)}</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-muted-foreground">Diff. Annuale</p>
                    <p className="font-mono font-bold text-amber-700">{formatCurrency(calculations.livello_suggerito.differenza_annuale)}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
